import { reaction } from "mobx";
import { observer } from "mobx-react";
import { Button, Tag, Tooltip, Typography, Row, Col } from "antd";

import * as MSF from "../../../../../components/OFTable/filters/MultiselectFilter";
import * as NF from "../../../../../components/OFTable/filters/NumericalFilter";
import * as SF from "../../../../../components/OFTable/filters/StateFilter";
import * as DRF from "../../../../../components/OFTable/filters/DateRangeFilter";

import OFTable from "../../../../../components/OFTable/OFTable";
import { CrudTableButtons } from "../../../_common/CrudTableButtons";
import { CrudDeveloperOptions } from "../../../_common/CrudDeveloperOptions";
import { UserUtilities } from "../../UserUtilities";

import copy from "copy-to-clipboard";
import _ from "lodash";

import { translateKey as t } from "../../../../../locales/translation";

import { UserController } from "../../UserController";
import { AppStore } from "~/stores/AppStore";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsSplitUpAndLeft,
  faBars,
  faCheckCircle,
  faCircleCheck,
  faCircleXmark,
  faCloud,
  faCopy,
  faCrown,
  faEdit,
  faEye,
  faHome,
  faHouse,
  faIdCard,
  faMessage,
  faTrash,
  faUser,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogle, faWindows } from "@fortawesome/free-brands-svg-icons";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const UserTable = observer(({ store, controller }: { store: AppStore; controller: UserController }) => {
  let aWeekAgo: any = dayjs().subtract(7, "d");
  let aYearAgo: any = dayjs().subtract(365, "d");
  let utilities: UserUtilities = new UserUtilities();

  const getRowIndex = (row: any) => {
    return controller.users.findIndex((du) => du._id === row._id) + 1;
  };

  const getRole = (ref: string) => {
    const role: any = controller.roles.find((r) => r.roleRef === ref);

    if (role) {
      return role.name;
    }

    return ref;
  };

  // Contains all the columns of the CRUD, including the additional ones (index, createdAt and updatedAtTimestamp)
  const structureWithAdditionalColumns = () => {
    let columns: Array<any> = [
      {
        Header: t("lblUsers", "Users"),
        id: "card",
        // disableSortBy: true,
        disableFilters: false,
        accessor: (row) => row,
        Cell: (data, value) => renderUsersInfo(data),
      },
      {
        Header: controller.isReadOnly ? t("lblView", "View") : t("lblEdit", "Edit"),
        id: "action",
        width: 40,
        disableSortBy: true,
        disableFilters: true,
        accessor: (row) => _.get(row, "_id"),
        Cell: (user) => (
          <Button
            onClick={() => controller.openEditForm(user)}
            icon={controller.isReadOnly ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEdit} />}
            size="small"
          />
        ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblIndex", "Index"),
        id: "index",
        align: "right",
        width: 40,
        accessor: (row) => getRowIndex(row),
        Filter: NF.NumericalFilter,
        filterMethod: NF.FilteringMethod,
        Cell: (row) => (
          <Typography.Text disabled style={{ cursor: "pointer" }}>
            {getRowIndex(row)}
          </Typography.Text>
        ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblActive", "Active"),
        width: 50,
        id: "active",
        accessor: (row: any) => _.get(row, "active", false),
        Filter: (props) => (
          <SF.StateFilter
            {...props}
            structure={[
              { title: t("lblActive", "Active"), value: true },
              { title: t("lblInactive", "Inactive"), value: false },
            ]}
          />
        ),
        filterMethod: (value, query) => value === query,
        Cell: (data, value) =>
          value ? (
            <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#52C41A" }} />
          ) : (
            <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />
          ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblUsername", "Username"),
        id: "username",
        width: 100,
        accessor: (row: any) => _.get(row, "username", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblEmail", "Email"),
        id: "email",
        width: 200,
        accessor: (row: any) => _.get(row, "email", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblFirstName", "First Name"),
        id: "firstName",
        width: 100,
        accessor: (row: any) => _.get(row, "firstName", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblLastName", "Last Name"),
        id: "lastName",
        width: 100,
        accessor: (row: any) => _.get(row, "lastName", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblProvider", "Provider"),
        id: "provider",
        width: 100,
        accessor: (row: any) => _.get(row, "provider", ""),
        Filter: (props) => {
          return (
            <MSF.MultiselectFilter
              {...props}
              dynamicValues={() => {
                const filter = [];
                controller.providers.forEach((p) => filter.push({ title: p.name, value: p.ref }));
                return filter;
              }}
            />
          );
        },
        filterMethod: MSF.FilteringMethod,
        Cell: (data, value) => {
          switch (value) {
            case "azuread":
              return (
                <Tag color="cyan" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faWindows} style={{ marginRight: 5 }} />
                  <span>Microsoft</span>
                </Tag>
              );
            case "google":
              return (
                <Tag color="magenta" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faGoogle} style={{ marginRight: 5 }} />
                  <span>Google</span>
                </Tag>
              );
            case "ldapauth":
              return (
                <Tag color="geekblue" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faArrowsSplitUpAndLeft} style={{ marginRight: 5 }} />
                  <span>LDAP</span>
                </Tag>
              );
            case "local":
              return (
                <Tag color="purple" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faHouse} style={{ marginRight: 5 }} />
                  <span>{t("lblLocal", "Local")}</span>
                </Tag>
              );
            case "mlauth":
              return (
                <Tag color="purple" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faCloud} style={{ marginRight: 5 }} />
                  <span>{t("lblMlauth", "MLAuth")}</span>
                </Tag>
              );
            default:
              return (
                <Tag style={{ margin: 0, cursor: "pointer" }}>
                  <span>{t("lblUnspecified", "Unspecified")}</span>
                </Tag>
              );
          }
        },
        responsiveRender: "lg",
      },
      {
        Header: t("lblRole", "Role"),
        id: "roles",
        width: 200,
        accessor: (row: any) => getRole(_.get(row, "roleRef")),
        Filter: (props) => (
          <MSF.MultiselectFilter
            {...props}
            dynamicValues={() => {
              const filter: Array<any> = [];

              controller.roles.forEach((r) =>
                filter.push({
                  title: r.name,
                  value: r.name,
                })
              );

              return filter;
            }}
          />
        ),
        filterMethod: MSF.FilteringMethod,
        Cell: (data, value) => {
          //Shows a warning if a user, created with the old UI, has more than one role (bad data)
          if (data.roles?.length > 1) {
            return (
              <>
                <span>{value}</span>
                <Tooltip
                  title={t("lblThisUserHasMoreThanOneRoleAssigned", "This User has more than one role assigned!")}
                >
                  <FontAwesomeIcon icon={faWarning} style={{ marginLeft: 5, color: "#faad14" }} />
                </Tooltip>
              </>
            );
          } else {
            return value;
          }
        },
        responsiveRender: "lg",
      },
      {
        Header: (
          <Tooltip
            title={`${t("lblLastSuccessfulLogin", "Last Successful Login")} (${t(
              "lblFormat",
              "Format"
            )}: DD/MM/YYYY HH:mm)`}
          >
            {t("lblLastLogin", "Last Login")}
          </Tooltip>
        ),
        id: "lastSuccessfulLogin",
        width: 150,
        accessor: (row) =>
          _.get(row, "lastSuccessfulLogin", "")
            ? dayjs(_.get(row, "lastSuccessfulLogin")).format("DD/MM/YYYY HH:mm")
            : "",
        rawValue: (row) =>
          _.get(row, "lastSuccessfulLogin", 0) ? dayjs(_.get(row, "lastSuccessfulLogin")).valueOf() : 0,
        excludeZero: true,
        Filter: (props) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value, query) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        // Cell: (data, value) => value
        Cell: (data, value) => {
          // Less than a week ago
          if (dayjs(value, "DD/MM/YYYY HH:mm").isAfter(aWeekAgo)) {
            return (
              <div>
                <span>{value}&nbsp;&nbsp;</span>
                <Tooltip title={t("lblLessThanAWeekAgo", "Less than a week ago")}>
                  <Tag color="green" style={{ margin: 0, cursor: "help" }}>
                    <span>D</span>
                  </Tag>
                </Tooltip>
              </div>
            );
          }
          // More than a week ago
          else if (dayjs(value, "DD/MM/YYYY HH:mm").isBetween(aYearAgo, aWeekAgo)) {
            return (
              <div>
                <span>{value}&nbsp;&nbsp;</span>
                <Tooltip title={t("lblMoreThanAWeekAgo", "More than a week ago")}>
                  <Tag color="orange" style={{ margin: 0, cursor: "help" }}>
                    <span>W</span>
                  </Tag>
                </Tooltip>
              </div>
            );
          }
          // More than a year ago
          else if (dayjs(value, "DD/MM/YYYY HH:mm").isBefore(aYearAgo)) {
            return (
              <div>
                <span>{value}&nbsp;&nbsp;</span>
                <Tooltip title={t("lblMoreThanAYearAgo", "More than a year ago")}>
                  <Tag color="red" style={{ margin: 0, cursor: "help" }}>
                    <span>Y</span>
                  </Tag>
                </Tooltip>
              </div>
            );
          } else {
            return value;
          }
        },
        responsiveRender: "lg",
      },
      {
        Header: (
          <Tooltip title={`${t("lblCreatedAt", "Created At")} (${t("lblFormat", "Format")}: DD/MM/YYYY HH:mm)`}>
            {t("lblCreatedAt", "Created At")}
          </Tooltip>
        ),
        id: "createdAt",
        width: 150,
        accessor: (row) =>
          _.get(row, "createdAt", "") ? dayjs(_.get(row, "createdAt")).format("DD/MM/YYYY HH:mm") : "",
        rawValue: (row) => (_.get(row, "createdAt", 0) ? dayjs(_.get(row, "createdAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value, query) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: (
          <Tooltip title={`${t("lblUpdatedAt", "Updated At")} (${t("lblFormat", "Format")}: DD/MM/YYYY HH:mm)`}>
            {t("lblUpdatedAt", "Updated At")}
          </Tooltip>
        ),
        id: "updatedAtTimestamp",
        width: 150,
        accessor: (row) =>
          _.get(row, "updatedAtTimestamp", "")
            ? dayjs(_.get(row, "updatedAtTimestamp")).format("DD/MM/YYYY HH:mm")
            : "",
        rawValue: (row) =>
          _.get(row, "updatedAtTimestamp", 0) ? dayjs(_.get(row, "updatedAtTimestamp")).valueOf() : 0,
        excludeZero: true,
        Filter: (props) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value, query) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: t("lblUpdatedBy", "Updated By"),
        id: "updatedBy",
        width: 150,
        accessor: (row: any) => _.get(row, "updatedBy", ""),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
    ];

    return columns;
  };

  // A lighter structure, which does not contain the additional columns (index, createdAt and updatedAtTimestamp)
  const structureWithoutAdditionalColumns = () => {
    return structureWithAdditionalColumns().filter(
      (item) =>
        item.id !== "index" && item.id !== "createdAt" && item.id !== "updatedAtTimestamp" && item.id !== "updatedBy"
    );
  };

  const buildTableColumns = () => {
    // Changes the table structure when the "Show/Hide Additional Columns" button is clicked
    reaction(
      () => controller.additionalColumnsAreVisible,
      (additionalColumnsAreVisible) => {
        if (additionalColumnsAreVisible) {
          if (controller?.tableInstance) controller.tableInstance.computeColumns(structureWithAdditionalColumns());
        } else {
          controller.tableInstance.computeColumns(structureWithoutAdditionalColumns());
        }
      }
    );

    // The "light" structure is shown by default, when the CRUD loads
    return structureWithoutAdditionalColumns();
  };

  const getContextMenuObj = () => {
    return {
      title: null,
      tagObj: { title: "username" },
      singleMenus: (row) => [
        {
          name: controller.isReadOnly ? t("lblView", "View") : t("lblEdit", "Edit"),
          type: "item",
          icon: controller.isReadOnly ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEdit} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          func: () => controller.handleContextMenuEdit(row),
        },
        {
          name: t("lblDelete", "Delete"),
          type: "item",
          icon: <FontAwesomeIcon icon={faTrash} />,
          render: true,
          disabled: controller.isReadOnly,
          func: () => controller.handleContextMenuDelete(row),
        },
        {
          name: t("lblCopyToClipboard", "Copy to Clipboard"),
          type: "item",
          icon: <FontAwesomeIcon icon={faCopy} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          func: () => copyRow(row),
        },
        {
          name: t("lblOther", "Other"),
          type: "subMenu",
          icon: <FontAwesomeIcon icon={faBars} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          subMenus: [
            {
              name: t("lblCopyUsername", "Copy Username"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.username),
            },
            {
              name: t("lblCopyEmail", "Copy Email"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.email),
            },
            {
              name: t("lblCopyFirstName", "Copy First Name"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.firstName),
            },
            {
              name: t("lblCopyLastName", "Copy Last Name"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.lastName),
            },
            {
              name: t("lblCopyLastLogin", "Copy Last Login"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: !row.lastSuccessfulLogin,
              func: () =>
                controller.copyText(
                  dayjs(_.get(row, "lastSuccessfulLogin", "")).isValid()
                    ? dayjs(_.get(row, "lastSuccessfulLogin", "")).format("DD/MM/YYYY HH:mm")
                    : ""
                ),
            },
          ],
        },
      ],
    };
  };

  // Copy the row data in a user-friendly text format
  const copyRow = (row) => {
    let copyText: string = "";

    copyText += t("lblUser", "User") + "\n";
    copyText += "● " + t("lblActive", "Active") + ": " + String(row.active) + "\n";
    copyText += "● " + t("lblUsername", "Username") + ": " + row.username + "\n";
    copyText += "● " + t("lblEmail", "Email") + ": " + row.email + "\n";
    copyText += "● " + t("lblFirstName", "First Name") + ": " + row.firstName + "\n";
    copyText += "● " + t("lblLastName", "Last Name") + ": " + row.lastName + "\n";
    copyText +=
      "● " +
      t("lblProvider", "Provider") +
      ": " +
      controller.providers.find((p) => p.ref === row.provider)?.name +
      "\n";
    copyText += "● " + t("lblRole", "Role") + ": " + getRole(_.get(row, "roleRef")) + "\n";
    row.lastSuccessfulLogin &&
      (copyText +=
        "● " +
        t("lblLastLogin", "Last Login") +
        ": " +
        (dayjs(_.get(row, "lastSuccessfulLogin", "")).isValid()
          ? dayjs(_.get(row, "lastSuccessfulLogin", "")).format("DD/MM/YYYY HH:mm")
          : "") +
        "\n");
    // copyText += "● " + t("lblDepots", "Depots") + ": " + this.getDepotsExportMarkup(_.get(row, "depots"));

    copy(copyText);
    store.displayNotification(t("lblRowCopiedToClipboard", "Row Copied to Clipboard"), "info", false);
  };

  //--------------------------------------------------------------------------------------------------------------

  const renderUsersInfo = (user) => {
    let proider;

    switch (_.get(user, "provider", "")) {
      case "azuread":
        proider = (
          <Tag color="cyan" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faWindows} style={{ marginRight: 5 }} />
            <span>Microsoft</span>
          </Tag>
        );
        break;
      case "google":
        proider = (
          <Tag color="magenta" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faGoogle} style={{ marginRight: 5 }} />
            <span>Google</span>
          </Tag>
        );
        break;
      case "ldapauth":
        proider = (
          <Tag color="geekblue" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faArrowsSplitUpAndLeft} style={{ marginRight: 5 }} />
            <span>LDAP</span>
          </Tag>
        );
        break;
      case "local":
        proider = (
          <Tag color="purple" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faHome} style={{ marginRight: 5 }} />
            <span>{t("lblLocal", "Local")}</span>
          </Tag>
        );
        break;
      case "mlauth":
        proider = (
          <Tag color="purple" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faCloud} style={{ marginRight: 5 }} />
            <span>{t("lblMlauth", "MLAuth")}</span>
          </Tag>
        );
        break;
      default:
        proider = (
          <Tag style={{ margin: 0, cursor: "pointer" }}>
            <span>{t("lblUnspecified", "Unspecified")}</span>
          </Tag>
        );
        break;
    }

    return (
      <div style={{ textAlign: "left" }}>
        <div style={{ fontWeight: "bold", textAlign: "left", borderBottom: "1px solid #000", marginBottom: 5 }}>
          <Row gutter={16} style={{ padding: 5 }}>
            <Col span={12}>
              <FontAwesomeIcon icon={faUser} style={{ marginRight: 10 }} />
              {_.get(user, "username", "")}
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              {user?.active ? (
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#52C41A" }} />
              ) : (
                <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />
              )}
            </Col>
          </Row>
        </div>

        <Row gutter={16} style={{ padding: "0px 5px" }}>
          <Col span={12}>
            <div className="truncate">
              <FontAwesomeIcon icon={faIdCard} style={{ marginRight: 5 }} />
              {_.get(user, "firstName", "")} {_.get(user, "lastName", "")}
            </div>
          </Col>

          <Col span={12}>
            <div className="truncate">
              <FontAwesomeIcon icon={faMessage} style={{ marginRight: 5 }} />
              {_.get(user, "email", "")}
            </div>
          </Col>
        </Row>

        <Row gutter={16} style={{ padding: "5px 5px 0px 5px" }}>
          <Col className="gutter-row" span={12}>
            {proider}
          </Col>
          <Col className="gutter-row" span={12}>
            <div className="truncate">
              <FontAwesomeIcon icon={faCrown} style={{ marginRight: 5 }} />
              {getRole(_.get(user, "roleRef"))}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const renderUsersTable = () => {
    const { isReadOnly, crudCommon, openEditForm } = controller;

    // Removes "To edit the cell - double click on it" info
    const tableInfo: Array<any> = crudCommon.renderTableInfo().filter((info) => info.key !== 1);

    return (
      <OFTable
        ndnConfig={null}
        controller={controller}
        columns={buildTableColumns()}
        dataObj="users"
        exportName={t("lblUsers", "Users")}
        tableInstance={(instance) => controller.setTableInstance(instance)}
        showCheckbox={!isReadOnly}
        editableCells={false} //editableCells={!isReadOnly}
        globalSearch={true}
        tableInfo={tableInfo}
        fixedColumns={4}
        tableCardHeight={85}
        onCardClick={(row) => openEditForm(row)}
        onRowClick={(row) => crudCommon.handleRowClick(row)}
        onMultiSelection={() => crudCommon.handleMultiselection()}
        multiSelectRowObj="multiSelectKeyPressed"
        shiftKeyFlag="shiftKeyPressed"
        menuButtons={[
          <CrudTableButtons
            controller={controller}
            key={"CrudTableButtons"}
            onDelete={() => controller.deleteUsers()}
            onCreate={() => controller.setNewUser()}
            createButtonTitle={t("lblUser", "User")}
          />,
          <CrudDeveloperOptions
            controller={controller}
            key="CrudDeveloperOptions"
            item="user"
            itemName="User"
            collectionName="users"
            fillCreateForm={utilities.fillCreateFormData}
            v1PageLink="/legacy/users"
            // model={this.utilities.modelHTML}
            apiCallsList={utilities.apiCalls}
            representativeField="username"
          />,
        ]}
        contextMenuObj={getContextMenuObj()}
        contextMenuWidth={200}
      />
    );
  };

  return renderUsersTable();
});

export { UserTable };
